import { API, graphqlOperation } from "aws-amplify";

import {
  gateCheckInByShipment,
  gateCheckInByYardByStatus,
} from "../../../graphql/gatecheckin/custom-queries";
import {
  createGateCheckInMini,
  updateGateCheckInMini,
} from "../../../graphql/gatecheckin/custom-mutations";
import { updateInboundShipmentMini } from "../../../graphql/inbound/custom-mutations";
import { getInboundShipmentGatePlacement } from "../../../graphql/inbound/custom-queries";
import { updateOutboundShipmentMini } from "../../../graphql/outbound/custom-mutations";
import { getOutboundShipmentGatePlacement } from "../../../graphql/outbound/custom-queries";
import { getGateCheckIn } from "../../../graphql/queries";
import {
  fetchAllPages,
  isConditionalCheckFailed,
  logError,
} from "../../../utils/appUtils";
import {
  GateCheckInStatus,
  InboundStatus,
  NoteType,
  OutboundStatus,
} from "../../../utils/constants";
import { applyStopRules } from "../../../utils/driverStops";
import {
  sessionYardDriverStops,
  sessionYardOpeningGateStatus,
  sessionYardRunsDriverCheckIn,
} from "../../../utils/gateAccess";
import {
  canSetGateDeparture,
  canSetGateStatus,
  departureNeedsDockReason,
  gateDepartureRefusal,
  departureNeedsWorkDoneReason,
  isShipmentWorkDone,
  dedupeActiveCheckIns,
  isBoundByQueueOrder,
  nextInGateQueue,
  outOfTurnTooltip,
  sortByGateServiceTime,
} from "../../../utils/gateQueueOrder";
import { isAlreadyPlaced } from "../../../utils/shipmentPlacement";
import {
  groupCheckInShipmentIds,
  isLinked,
  shipWithSiblingIds,
} from "../../../utils/shipWith";
import { ensureCompositeKeyFields } from "../../../utils/trackCarPersistence";
import { fetchGateShipments, stampPlacementFromGate } from "./gatePlacement";

export const GATE_ACTIVE_STATUSES = [
  GateCheckInStatus.ARRIVED,
  GateCheckInStatus.CALLED,
  GateCheckInStatus.DOCKED,
];
export const GATE_TERMINAL_STATUSES = [
  GateCheckInStatus.DEPARTED,
  GateCheckInStatus.CANCELLED,
];
export const GATE_ALL_STATUSES = [
  ...GATE_ACTIVE_STATUSES,
  ...GATE_TERMINAL_STATUSES,
];

/** Where a spotted load is worked in the Operator App. Both crew process pages
 * load a shipment by id, so the deep link stands on its own — no list state has
 * to be primed first. Shared by the manager Gate Queue's Open button and the
 * crew Gate Queue's jump after Set Placed, so a truck opened from the office
 * and a truck opened from the yard floor land on the same page. */
export const placedShipmentPath = (shipmentId, isOutbound) =>
  isOutbound
    ? `/processOutboundShipments/${shipmentId}`
    : `/processShipments/${shipmentId}`;

/** Where a gate row's shipment is opened in the Manager Portal: the edit form,
 * the same route the shipment lists link to. The counterpart to
 * placedShipmentPath — which portal a supervisor or manager is working decides
 * which of the two the Gate Queue's Open button uses. */
export const shipmentEditPath = (shipmentId, isOutbound) =>
  isOutbound
    ? `/outboundShipments/${shipmentId}`
    : `/inboundShipments/${shipmentId}`;

/** Raised when the row moved on between the read and the write — the same
 * check-in is editable from the Gate Queue page and from either shipment's
 * dispatch tab, so a form left open can hold a status the gate has already
 * advanced past. Callers refresh and let the user retry rather than clobber. */
export class GateCheckInConflictError extends Error {
  constructor(message = "Check-in changed at the gate") {
    super(message);
    this.name = "GateCheckInConflictError";
  }
}

/** Raised when the check-in row is written and standing on the Gate Queue but
 * the shipment lock could not be claimed for it. The row is real and the crew
 * can see it; what is missing is the shipment's pointer at it, and that pointer
 * is the only thing stopping the driver API — which gates on the lock alone —
 * from minting a second row for the same truck. Callers say that, rather than
 * reporting a row that was never created. `cause` carries the write error that
 * got us here, so the log still names the transient failure. */
export class GateLockNotClaimedError extends Error {
  constructor(cause) {
    super("Gate check-in created but not linked to the shipment");
    this.name = "GateLockNotClaimedError";
    this.cause = cause;
  }
}

/** Raised when a user who calls drivers in the published order (a supervisor)
 * tries to advance a truck that is not at the front of the queue. The message
 * is the one both queue pages show as a tooltip, so the refusal reads the same
 * whether it arrives before the click or after it. */
export class GateQueueOrderError extends Error {
  constructor(message) {
    super(message || outOfTurnTooltip());
    this.name = "GateQueueOrderError";
  }
}

/** Raised when a visit is ended as DEPARTED from a status where the truck
 * never reached a dock. Unlike the order refusal this is not a race and not
 * about who is asking — the row cannot carry that claim at all — so the
 * message names what ends the visit instead of asking the user to try again. */
export class GateDepartureNotDockedError extends Error {
  constructor() {
    super(departureNeedsDockReason());
    this.name = "GateDepartureNotDockedError";
  }
}

/** Raised when a docked truck is marked DEPARTED while its load is still
 * being worked (no end time, shipment not COMPLETED). Same footing as the
 * dock rule: about the visit, not the viewer, and not a retry. A linked truck
 * carries several loads, so the message then names the one still on the dock
 * rather than the generic reason. */
export class GateDepartureWorkPendingError extends Error {
  constructor(message) {
    super(message || departureNeedsWorkDoneReason());
    this.name = "GateDepartureWorkPendingError";
  }
}

/** Every shipment a check-in row controls: an inbound row's one load, or an
 * outbound row's searched load plus, on a linked truck (LTL "ship with"),
 * every load in `outboundIds`. The terminal statuses release, cancel rolls
 * back, and the manual mint claims, one member at a time from this list. */
const checkInShipmentIds = (checkIn) =>
  checkIn?.inboundId ? [checkIn.inboundId] : groupCheckInShipmentIds(checkIn);

/** How a load is named in a message about a linked truck, where the refusal
 * has to say which of several loads it means. The gate projection carries no
 * PO number today, so the id stands in until it does. */
const loadLabel = (shipment) =>
  shipment?.poNumber ? `PO ${shipment.poNumber}` : `Load ${shipment?.id}`;

/**
 * Every check-in the yard holds in any of `statuses`, one index read per
 * status, all pages of each.
 *
 * The index is keyed reloadYardId + status#arrivedAt, so a status is one
 * query rather than a filter over the whole yard. Every page of it matters: a
 * truck dropped off the end of the first page loses its gate controls, and it
 * loses them silently — the row is simply not in the list. It can also change
 * who the queue says is next, because a place in line is the LATER of the
 * appointment and the check-in, so a truck that checked in last can still be
 * the one to call first.
 *
 * Ordering is left to the caller: the pages come back in arrivedAt order
 * within a status, which is not queue order across statuses.
 */
export const fetchGateCheckInsByStatus = async (reloadYardId, statuses) => {
  const lists = await Promise.all(
    statuses.map((status) =>
      fetchAllPages({
        query: gateCheckInByYardByStatus,
        variables: {
          reloadYardId,
          statusArrivedAt: { beginsWith: { status } },
          sortDirection: "ASC",
        },
        responseKey: "gateCheckInByYardByStatus",
        label: `gateCheckIns:${status}`,
      }),
    ),
  );
  return lists.flat();
};

/** The trucks still waiting at the gate, in queue order. One status: the order
 * only decides which WAITING row may be called next, and a row already CALLED
 * or DOCKED is through the gate and out of the running. */
export const fetchWaitingGateCheckIns = async (reloadYardId) =>
  sortByGateServiceTime(
    dedupeActiveCheckIns(
      await fetchGateCheckInsByStatus(reloadYardId, [
        GateCheckInStatus.ARRIVED,
      ]),
    ),
    (c) => [c.scheduledDateTime, c.arrivedAt],
  );

/**
 * Refuse a supervisor's out-of-turn call at the write, not just in the menu.
 *
 * The queue pages grey out the statuses the order forbids, but greying is a
 * render-time courtesy: the dispatch tabs offer the same status select against
 * a single check-in with no queue in view, and any page's idea of who is next
 * is as old as its last poll. So the rule is re-tested here, against the queue
 * as it stands at the moment of the write.
 *
 * Costs a query only when it can change the answer - the viewer is bound by
 * the order, and the move is the one the order governs (calling a waiting
 * truck forward). Every other save writes straight through.
 *
 * Nothing here is swallowed. A failed read, or no yard to read the queue from,
 * leaves the turn unverified, and an unverifiable turn is not a granted one —
 * the caller reports it the same way it reports any other failed save.
 */
export const assertGateQueueTurn = async (checkIn, status) => {
  if (!isBoundByQueueOrder(window.appUser?.type)) return;
  if (
    canSetGateStatus({
      status,
      currentStatus: checkIn.status,
      orderBound: true,
      isNextInQueue: false,
    })
  )
    return;

  const reloadYardId = checkIn.reloadYardId || window.appUser?.reloadYardId;
  // Unreachable as things stand: SUPERVISOR is the only type the order binds,
  // and the yard portal a supervisor signs into is routed on
  // appUser.reloadYardId, so a session that gets this far always carries one.
  // Refused rather than waved through anyway, because the guard is worth what
  // it is worth on the day a second user type is bound by the order and
  // reaches here without a yard: the queue is unread, so the turn is unproven.
  if (!reloadYardId)
    throw new Error("Gate queue order could not be checked: no reload yard");
  const waiting = await fetchWaitingGateCheckIns(reloadYardId);
  const next = nextInGateQueue(waiting, (c) => c.status);
  // No waiting row at all means the queue disagrees with the row in hand
  // (it should be in that list) — let the write's own condition settle it.
  if (!next || next.id === checkIn.id) return;
  throw new GateQueueOrderError(
    outOfTurnTooltip(next.driverName || next.carrierName),
  );
};

/** Release one shipment's gateCheckInId lock for a row. Conditioned on the
 * lock still being OURS — a newer check-in must not lose its lock to a stale
 * row's completion. */
const releaseMemberLock = async (checkIn, shipmentId, outbound) => {
  const mutation = outbound
    ? updateOutboundShipmentMini
    : updateInboundShipmentMini;
  try {
    await API.graphql(
      graphqlOperation(mutation, {
        input: { id: shipmentId, gateCheckInId: null },
        condition: { gateCheckInId: { eq: checkIn.id } }, // only release OUR lock
      }),
    );
  } catch (err) {
    if (!isConditionalCheckFailed(err)) {
      logError("gateCheckInUtils:releaseLock", err, false);
    }
  }
};

/** Terminal statuses release the shipment's gateCheckInId lock so the next
 * driver can check in (plan AD-7) — for every shipment the row controls, one
 * conditional write each. A linked truck's row holds the lock on each of its
 * loads, and the truck leaving frees all of them. */
export const releaseShipmentLock = async (checkIn) => {
  const outbound = !checkIn.inboundId;
  for (const shipmentId of checkInShipmentIds(checkIn)) {
    await releaseMemberLock(checkIn, shipmentId, outbound);
  }
};

/** The shipment as the gate reads it: the projection both gate surfaces already
 * share, which carries the check-in lock (gateCheckInId) alongside every stamp
 * isAlreadyPlaced tests. Returns null when the shipment is gone; throws when
 * the read itself fails, so a caller settling an ambiguous write can tell "no
 * lock" from "could not look". */
const fetchShipmentGateState = async (shipmentId, outbound) => {
  const response = await API.graphql(
    graphqlOperation(
      outbound
        ? getOutboundShipmentGatePlacement
        : getInboundShipmentGatePlacement,
      { id: shipmentId },
    ),
  );
  return (
    (outbound
      ? response?.data?.getOutboundShipment
      : response?.data?.getInboundShipment) || null
  );
};

/** The cancel rollback for one shipment the check-in controls. Rolls it back
 * to its pre-gate status and clears the arrival stamp — but only when it is
 * still exactly where the check-in left it: status ARRIVED, never placed, and
 * the lock is still this check-in's. Anything else (placed meanwhile, a newer
 * check-in) falls back to a plain lock release for that one shipment. */
const rollbackMemberArrival = async (checkIn, shipmentId, outbound) => {
  try {
    const shipment = await fetchShipmentGateState(shipmentId, outbound);
    const arrivedStatus = outbound
      ? OutboundStatus.ARRIVED
      : InboundStatus.ARRIVED;
    const placedStamp = outbound
      ? shipment?.placementDateTime
      : shipment?.placedDateTime;
    if (
      !shipment ||
      shipment.status !== arrivedStatus ||
      placedStamp ||
      shipment.gateCheckInId !== checkIn.id
    ) {
      return releaseMemberLock(checkIn, shipmentId, outbound);
    }

    const historyNote = (statusName) => [
      ...(shipment.history || []),
      {
        type: NoteType.SHIPMENT,
        text: `Gate check-in cancelled — returned to ${statusName}`,
        date: new Date().toISOString(),
        by: window.appUser?.fullName,
      },
    ];

    // status and the arrival stamp sit in composite-key GSIs — echo every
    // member so the update resolver recomposes the synthetic sort keys.
    const input = outbound
      ? ensureCompositeKeyFields(
          {
            id: shipment.id,
            status: OutboundStatus.PENDING,
            arrivalDateTime: null,
            endDateTime: shipment.endDateTime ?? null,
            scheduledDateTime: shipment.scheduledDateTime ?? null,
            createDateTime: shipment.createDateTime ?? null,
            active: shipment.active,
            gateCheckInId: null,
            history: historyNote("Pending"),
          },
          true,
        )
      : ensureCompositeKeyFields(
          {
            id: shipment.id,
            status: InboundStatus.IN_TRANSIT,
            // The check-in stamped notifyDateTime as the gate arrival; the
            // cancel clears it with the status.
            notifyDateTime: null,
            releaseDateTime: shipment.releaseDateTime ?? null,
            placedDateTime: shipment.placedDateTime ?? null,
            startDateTime: shipment.startDateTime ?? null,
            endDateTime: shipment.endDateTime ?? null,
            scheduledDateTime: shipment.scheduledDateTime ?? null,
            createDateTime: shipment.createDateTime ?? null,
            gateCheckInId: null,
            history: historyNote("In Transit"),
          },
          false,
        );
    await API.graphql(
      graphqlOperation(
        outbound ? updateOutboundShipmentMini : updateInboundShipmentMini,
        {
          input,
          // only undo OUR arrival, and only while nothing else moved the row
          condition: {
            status: { eq: arrivedStatus },
            gateCheckInId: { eq: checkIn.id },
          },
        },
      ),
    );
  } catch (err) {
    if (!isConditionalCheckFailed(err)) {
      logError("gateCheckInUtils:rollbackCancelledArrival", err, false);
    }
    // The check-in row is already CANCELLED — at minimum free the lock.
    return releaseMemberLock(checkIn, shipmentId, outbound);
  }
};

/** Cancelling a check-in undoes what the check-in did to the shipment: the
 * driver API advances the shipment to ARRIVED (outbound from PENDING, inbound
 * from IN_TRANSIT) and stamps its arrival, so a cancelled driver must not
 * leave a phantom truck in the Arrived tab and the crew Gate Queue. Every
 * shipment the row controls is judged on its own — a linked truck's row
 * arrived all of its loads, and each is rolled back only if it is still where
 * the check-in left it (rollbackMemberArrival), else its lock is released. */
const rollbackCancelledArrival = async (checkIn) => {
  const outbound = !checkIn.inboundId;
  for (const shipmentId of checkInShipmentIds(checkIn)) {
    await rollbackMemberArrival(checkIn, shipmentId, outbound);
  }
};

/** Persist an edit to a check-in row and, on a terminal status, release the
 * shipment lock. `edit` may carry {status, dockAssignment, driverName,
 * truckPlate, shipmentPlaced}. Returns the saved row so the caller can update
 * state without a refetch. Throws GateCheckInConflictError when another
 * surface got there first, and GatePlacementBlockedError when the move is to
 * DOCKED and the shipment cannot be placed — nothing is written then.
 *
 * `shipmentPlaced: true` says the caller placed the shipment itself in this
 * same action (the crew Gate Queue's Set Placed, which places first and docks
 * second), so the placement step here is skipped rather than re-read.
 *
 * The yard's stop route shapes the edit before any rule reads it
 * (applyStopRules): a call with no stop goes to the first route stop, a stop
 * assigned to a waiting driver is the call, a dock with no stop names the
 * last. Applied here as well as in the controls so a caller that stages only
 * half the pair still writes a whole one. */
export const saveGateCheckIn = async (checkIn, edit = {}) => {
  const { status, dockAssignment } = applyStopRules({
    stops: sessionYardDriverStops(),
    current: checkIn,
    status: edit.status || checkIn.status,
    dockAssignment:
      edit.dockAssignment !== undefined
        ? edit.dockAssignment
        : checkIn.dockAssignment,
  });
  // Before anything is written: a visit only completes from the dock. Tested
  // here and not only in the menus because this is the single writer every
  // surface goes through, and a greyed menu item is a render-time courtesy —
  // a select drawn before a poll moved the row, or a caller that forgot the
  // rule, would otherwise stamp completedAt on a truck that never docked.
  if (!canSetGateDeparture({ status, currentStatus: checkIn.status }))
    throw new GateDepartureNotDockedError();
  // And only after the load is worked: a departure over a load the yard still
  // holds is the same false history as one over an undocked truck. Read here
  // because the row carries nothing about the load; a row naming no shipment
  // has nothing to ask. Skipped for a re-save of DEPARTED (records edit). A
  // linked truck departs once for every load it carries, so every one of them
  // has to be worked, and the refusal names the one that is not.
  if (
    status === GateCheckInStatus.DEPARTED &&
    checkIn.status !== GateCheckInStatus.DEPARTED
  ) {
    const found = await fetchGateShipments(checkIn);
    const pending = found.find(
      ({ shipment }) => !isShipmentWorkDone(shipment),
    );
    if (pending)
      throw new GateDepartureWorkPendingError(
        found.length > 1
          ? `${loadLabel(pending.shipment)} is still being worked`
          : undefined,
      );
  }
  // Then the order: a supervisor may only call the truck at the front. Throws
  // GateQueueOrderError when this is not that truck.
  await assertGateQueueTurn(checkIn, status);
  // Then the load: DOCKED means the shipment is spotted, so it is placed
  // before the row is written, and a placement that is refused (no carrier or
  // trailer, an inbound with no inventory) refuses the dock too. The order
  // matters — a placed shipment whose driver still reads CALLED is a status
  // any gate surface can push again, while a DOCKED row over an unplaced
  // shipment is a truck the crew queue offers no button for. Same order the
  // crew page's Set Placed uses; it arrives here with the placement done.
  if (
    status === GateCheckInStatus.DOCKED &&
    checkIn.status !== GateCheckInStatus.DOCKED &&
    !edit.shipmentPlaced
  )
    await stampPlacementFromGate(checkIn);
  const input = {
    id: checkIn.id,
    status,
    // status is part of the gateCheckInByYardByStatus composite sort key —
    // the update resolver requires every key field when any one changes.
    arrivedAt: checkIn.arrivedAt,
    dockAssignment,
    updatedBy: window.appUser?.email || null,
  };
  // Who is driving and what they are driving. Sent only when edited, so a
  // save that touches the status alone cannot blank a name the gate recorded.
  if (edit.driverName !== undefined) input.driverName = edit.driverName || null;
  if (edit.truckPlate !== undefined) input.truckPlate = edit.truckPlate || null;
  if (
    status === GateCheckInStatus.CALLED &&
    checkIn.status !== GateCheckInStatus.CALLED
  )
    input.calledAt = new Date().toISOString();
  if (GATE_TERMINAL_STATUSES.includes(status))
    input.completedAt = new Date().toISOString();

  let response;
  try {
    response = await API.graphql(
      graphqlOperation(updateGateCheckInMini, {
        input,
        // Write only against the status we read. Two surfaces edit this row.
        condition: { status: { eq: checkIn.status } },
      }),
    );
  } catch (err) {
    if (isConditionalCheckFailed(err)) throw new GateCheckInConflictError();
    throw err;
  }

  if (status === GateCheckInStatus.CANCELLED)
    await rollbackCancelledArrival(checkIn);
  else if (GATE_TERMINAL_STATUSES.includes(status))
    await releaseShipmentLock(checkIn);

  return response?.data?.updateGateCheckIn || { ...checkIn, ...input };
};

/** Every check-in ever written for one shipment, newest first — the terminal
 * rows included, because "this visit already went through the gate" is a fact
 * only they carry. The index is hash-only (shipmentKey), so nothing orders the
 * rows but this sort and nothing bounds them but the pages: a row dropped off
 * the end would read as a visit that never happened. shipmentType is
 * "in" | "out" — the shipmentKey prefix the driver API writes.
 *
 * A row for a linked truck (LTL "ship with") is keyed under the load the
 * driver searched, which may be a sibling of the one asked about. A caller
 * that knows the group passes its ids as `groupIds`, and every member's key is
 * read and merged, so the row is found from any of its loads. Without them
 * the read is the one key it always was. */
const fetchShipmentCheckIns = async (shipmentType, shipmentId, groupIds) => {
  const keys = [shipmentId];
  (Array.isArray(groupIds) ? groupIds : []).forEach((id) => {
    if (id && !keys.includes(id)) keys.push(id);
  });
  const lists = await Promise.all(
    keys.map((id) =>
      fetchAllPages({
        query: gateCheckInByShipment,
        variables: { shipmentKey: `${shipmentType}:${id}` },
        responseKey: "gateCheckInByShipment",
        label: `gateCheckInByShipment:${shipmentType}:${id}`,
      }),
    ),
  );
  // One row lives under one key, so this only guards a caller that named the
  // same id twice; a row with no id is kept as read.
  const seen = new Set();
  const items = lists.flat().filter((row) => {
    if (!row?.id) return true;
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
  return items.sort((a, b) => (a.arrivedAt < b.arrivedAt ? 1 : -1));
};

/** One check-in row by id — the row a shipment's own `gateCheckInId` lock
 * points at. Null when it no longer resolves. */
export const fetchGateCheckInById = async (checkInId) => {
  if (!checkInId) return null;
  const response = await API.graphql(
    graphqlOperation(getGateCheckIn, { id: checkInId }),
  );
  return response?.data?.getGateCheckIn || null;
};

/** Active (non-terminal) check-in for one shipment, or null. Newest first: two
 * actives can only mean a stale row, so manage the real one.
 *
 * The shipment's own lock is the source of truth when the caller has it
 * (`checkInId`): the pointer names the row outright, where the index names
 * only the load the driver searched — a linked truck's row sits under a
 * sibling's key, and a surface that forgot to pass `groupIds` would miss it
 * there. A pointer that resolves to an active row wins; one that resolves to
 * nothing, to an ended visit, or that cannot be read falls back to the index
 * read this always was. */
export const fetchActiveGateCheckIn = async (
  shipmentType,
  shipmentId,
  groupIds,
  checkInId,
) => {
  if (checkInId) {
    try {
      const pointed = await fetchGateCheckInById(checkInId);
      if (pointed && GATE_ACTIVE_STATUSES.includes(pointed.status))
        return pointed;
    } catch (err) {
      logError("gateCheckInUtils:fetchActiveGateCheckIn:pointer", err, false);
    }
  }
  return (
    (await fetchShipmentCheckIns(shipmentType, shipmentId, groupIds)).find(
      (c) => GATE_ACTIVE_STATUSES.includes(c.status),
    ) || null
  );
};

/** Newest check-in for one shipment that was not cancelled, or null. Unlike
 * fetchActiveGateCheckIn this still finds the visit once the truck has
 * DEPARTED — a document generated after the fact wants the driver who came. */
export const fetchLatestGateCheckIn = async (shipmentType, shipmentId) =>
  (await fetchShipmentCheckIns(shipmentType, shipmentId)).find(
    (c) => c.status !== GateCheckInStatus.CANCELLED,
  ) || null;

/** The arrival stamp each direction's ARRIVED status derives from — the same
 * pair the crew Gate Queue reads to date its rows. */
export const arrivalStampOf = (shipment, outbound) =>
  outbound ? shipment?.arrivalDateTime : shipment?.notifyDateTime;

/** DynamoDB rejects NULL in a GSI key attribute (carrierUserId keys
 * gateCheckInByDriver), and an absent attribute reads back as null through
 * AppSync anyway — so an unknown value is omitted, never sent as null. Same
 * reason driverApi strips nulls before its Put. */
const withoutBlanks = (input) =>
  Object.fromEntries(
    Object.entries(input).filter(([, v]) => v !== null && v !== undefined),
  );

/** Retire a manual row that lost the lock race. Deliberately not
 * cancelGateCheckIn: that rolls the shipment back out of ARRIVED, and the
 * arrival here is the manager's own — it stands. Only the spare row goes.
 *
 * Only a row still waiting at the gate is a spare. Once a supervisor has called
 * the truck forward or the crew have it at a dock, that row is the visit the
 * yard is working and the row that won the lock is the newcomer — cancelling
 * the worked one would take a truck off the crew's queue mid-visit to keep a
 * driver who just walked up. Those are left standing and logged for a human to
 * reconcile on the Gate Queue: a truck listed twice is the cheaper wrong. Only
 * the adopt path can arrive here with a row past ARRIVED, since a row this
 * module just created is always ARRIVED. The write repeats the test as its
 * condition so a row called forward between this check and the write is spared
 * too. */
const retireUnlockedGateCheckIn = async (checkIn) => {
  if (checkIn.status !== GateCheckInStatus.ARRIVED) {
    logError(
      "gateCheckInUtils:retireUnlockedGateCheckIn",
      new Error(
        `Gate check-in ${checkIn.id} lost the shipment lock at status ${checkIn.status} — left active, so this truck now shows twice on the Gate Queue`,
      ),
      false,
    );
    return;
  }
  try {
    await API.graphql(
      graphqlOperation(updateGateCheckInMini, {
        input: {
          id: checkIn.id,
          status: GateCheckInStatus.CANCELLED,
          arrivedAt: checkIn.arrivedAt, // composite sort-key member
          completedAt: new Date().toISOString(),
        },
        condition: { status: { eq: GateCheckInStatus.ARRIVED } },
      }),
    );
  } catch (err) {
    logError("gateCheckInUtils:retireUnlockedGateCheckIn", err, false);
  }
};

/**
 * Claim the shipment's check-in lock (AD-7) for a row, and only while it is
 * free — Amplify removes the attribute when the lock is released, so its
 * absence is the whole test.
 *
 * Two writes, no transaction, so a failed claim has to be sorted out by hand
 * and one read is what sorts it. A conditional failure means the lock is
 * held — by a driver who won the race, or by this very row on a retry whose
 * first attempt landed after all. An ambiguous failure (transient
 * AppSync/network) says nothing about whether the write landed, and the same
 * read answers both.
 *
 * A row left active with no lock behind it is the state worth avoiding: the
 * driver API gates on the shipment's lock alone, so it would mint a second
 * check-in for the same truck. When the read cannot settle it, the row is left
 * for the next save to adopt rather than cancelled — the arrival it records is
 * real, and the caller warns.
 *
 * @param retry claim once more when the read leaves the lock free; false on
 *   that second attempt so a state this function cannot settle still surfaces
 * @returns the row when the lock is ours, or null when the row is not ours —
 *   retired if it was still waiting at the gate, left standing and logged if
 *   the yard had already called it forward
 * @throws GateLockNotClaimedError when two attempts both end with the lock
 *   free — the row stands, unlinked, for the next save to adopt
 */
const claimShipmentLock = async (shipment, checkIn, outbound, retry = true) => {
  try {
    await API.graphql(
      graphqlOperation(
        outbound ? updateOutboundShipmentMini : updateInboundShipmentMini,
        {
          input: { id: shipment.id, gateCheckInId: checkIn.id },
          condition: { gateCheckInId: { attributeExists: false } },
        },
      ),
    );
    return checkIn;
  } catch (err) {
    const conditional = isConditionalCheckFailed(err);
    let fresh = null;
    let readFailed = false;
    try {
      fresh = await fetchShipmentGateState(shipment.id, outbound);
    } catch (readErr) {
      readFailed = true;
      logError("gateCheckInUtils:claimShipmentLock", readErr, false);
    }
    // The write landed, or an earlier attempt's did. Nothing to undo.
    if (fresh?.gateCheckInId === checkIn.id) return checkIn;
    // Somebody else owns the shipment — or the lock was definitely held and we
    // could not read who by. Either way this row is the spare, and a spare left
    // active shows the truck twice in the gate queue.
    if (fresh?.gateCheckInId || (conditional && readFailed)) {
      await retireUnlockedGateCheckIn(checkIn);
      return null;
    }
    // Nothing holds the lock and the row is already written: this is the
    // orphan itself, one conditional write away from being healed, so claim
    // again rather than leave it for a save that may never come. Two ways in,
    // and the retry settles both. The claim really was lost, and the second
    // one takes the free lock outright. Or it landed and this read was simply
    // behind it — getInboundShipment is an ordinary eventually consistent
    // read — and then the retry fails its condition and the read behind that
    // returns our own id, so a manager stops being warned that a row which
    // exists and is sitting on the Gate Queue was never created. A driver who
    // took the lock in between is no risk either: attributeExists guards every
    // attempt, so the retry cannot take a lock that is not free. Once only —
    // a second attempt ending the same way is a state this function cannot
    // settle, and the caller's warning is the right answer to it.
    if (retry) return claimShipmentLock(shipment, checkIn, outbound, false);
    throw new GateLockNotClaimedError(err);
  }
};

/**
 * Claim the lock on every load a row controls, one conditional write each,
 * the saved shipment first. A single-load row is one claim, exactly as before.
 *
 * A linked truck's row is only whole once every member points at it — the
 * driver API gates on each member's lock, so a member left free is a load a
 * driver can still check in a second time. The claims are sequential, and the
 * first one that comes back "not ours" (claimShipmentLock retired the row, or
 * left it standing and logged) releases the locks already taken — they now
 * point at a row the yard is not going to work — and reports null. A claim
 * that cannot be settled throws GateLockNotClaimedError with the row standing
 * and the locks taken so far kept: the row is real, and the next save adopts
 * it and claims the rest (a member already ours claims as a no-op).
 */
const claimGroupLocks = async (shipment, checkIn, outbound, memberIds) => {
  const claimed = [];
  for (const id of memberIds) {
    const result = await claimShipmentLock(
      id === shipment.id ? shipment : { id },
      checkIn,
      outbound,
    );
    if (!result) {
      for (const heldId of claimed) {
        await releaseMemberLock(checkIn, heldId, outbound);
      }
      return null;
    }
    claimed.push(id);
  }
  return checkIn;
};

/**
 * The loads a manual row for a linked truck would control, read fresh, or
 * null when one of them cannot be checked in: every sibling has to be still
 * waiting for its truck (PENDING) — the same rule the kiosk applies
 * (LINKED_LOAD_NOT_ELIGIBLE) — and a sibling that no longer resolves cannot
 * be claimed. The saved shipment itself is judged by wantsManualGateCheckIn
 * before this is asked. Refusals are logged for the yard to reconcile: the
 * arrival the office typed stands on the one load, and the manager is told
 * the row was not created.
 */
const eligibleGroupMembers = async (shipment) => {
  const siblingIds = shipWithSiblingIds(shipment);
  const siblings = await Promise.all(
    siblingIds.map((id) => fetchShipmentGateState(id, true)),
  );
  const blocked = siblings.findIndex(
    (sibling) => !sibling || sibling.status !== OutboundStatus.PENDING,
  );
  if (blocked === -1) return [shipment.id, ...siblingIds];
  logError(
    "gateCheckInUtils:createManualGateCheckIn",
    new Error(
      `Gate check-in not created for ${shipment.id}: linked load ${
        siblingIds[blocked]
      } is ${siblings[blocked]?.status || "missing"}, not PENDING`,
    ),
    false,
  );
  return null;
};

/** Whether the office may still start a gate visit for this shipment.
 *
 * The form-side half of createManualGateCheckIn's decision, kept here so the
 * dispatch tab cannot offer the manager a check-in the save then declines to
 * mint. A shipment already holding a lock has its row — the tab shows it — and
 * one past spotting had the crew's half done by hand; createManualGateCheckIn
 * says why each is left alone.
 *
 * Not just the placement stamp: a load the crew finished working, or one
 * already released, was physically spotted whether or not anyone stamped the
 * placement, and legacy shipments carry exactly that shape — an arrival, a
 * finish or a release, no placement. isAlreadyPlaced is the same test every
 * other placement surface applies.
 *
 * Neither test reads the check-in history, so a visit the gate already finished
 * still reads as offerable here; only the create can afford the query that
 * settles it. */
export const canOfferManualGateCheckIn = (shipment, outbound) =>
  !shipment?.gateCheckInId && !isAlreadyPlaced(shipment, outbound);

/** Whether the shipment wants that row now. A gate check-in is an arrival, and
 * the arrival stamp is what dates the row (arrivedAt) and holds its place in
 * the queue order — a truck with no arrival on the form has nothing to be
 * checked in as, which is what the tab's Check In Truck button stamps. */
export const wantsManualGateCheckIn = (shipment, outbound) =>
  canOfferManualGateCheckIn(shipment, outbound) &&
  Boolean(arrivalStampOf(shipment, outbound));

/** Whether the form values on a dispatch tab describe a truck the office is
 * checking in — the state that makes the fields a check-in row is built from
 * required, on a form where they are otherwise optional.
 *
 * The kiosk refuses a check-in without a plate or a trailer number, so the two
 * paths have to ask for the same facts or the office one is the loophole:
 *   - carrier name rides onto the row (carrierName, carrierId) because the Gate
 *     Queue pages list check-ins, not shipments, and cannot re-read the
 *     shipment for a label. A manual row opens with no driver unless the office
 *     asked for one, so the carrier is often the only thing naming that truck
 *     to the crew.
 *   - railCarNumber is the trailer number: it becomes the row's own
 *     trailerNumber, the "Trl #" the crew Gate Queue prints, and what gate
 *     placement reads.
 * A yard not running the gate mints nothing and keeps the rules it had.
 *
 * The arrival stamp is half the question, not the staging flag alone. It is
 * what dates the row, what setGateEdit refuses to stage a manual edit without,
 * and what wantsManualGateCheckIn reads before the save mints anything — so an
 * office that backs out by clearing the arrival is no longer checking anybody
 * in. The flag stays behind in the form values, and asking it by itself left
 * the manager owing a trailer number and a carrier name for a row that would
 * never be written. It is also the stamp the dispatch tab's gate control
 * already reads for its pending state, so the fields and the control that asks
 * for them answer alike.
 *
 * @param values the form values — the arrival is the tab's own field, so the
 *   record does not have to be merged under them first
 * @param outbound which tab is asking, which picks the arrival field
 */
export const isStagingManualGateCheckIn = (values, outbound) =>
  Boolean(values?.gateCheckInEdit?.manual) &&
  Boolean(arrivalStampOf(values, outbound)) &&
  sessionYardRunsDriverCheckIn();

/**
 * The yard office marked a truck arrived that never reached the kiosk, so mint
 * the check-in row the kiosk would have written.
 *
 * At a yard running driver check-in the crew place a truck by the gate row —
 * they spot the driver a supervisor called forward, and CALLED is what tells
 * them to. A truck the office waves in has no row, and absent is not the same
 * fact as not-yet-called: without one the crew would wait on a signal nobody
 * can ever send. Writing the row makes every truck at the yard follow one
 * workflow whether the driver used the app or the office typed the arrival.
 *
 * Only for a truck still on its way to a dock, and only for a visit the gate
 * has not already finished. A manager who stamps the placement themselves has
 * already done the crew's half — that load goes straight to the
 * inbound/outbound basket, and a check-in row created behind it would put a
 * truck in the gate queue that is already spotted. A visit ended at the gate
 * leaves the same shape as one that never started, so the shipment's own
 * check-in history is what tells them apart.
 *
 * What the row cannot know, it leaves empty rather than inventing: no driver
 * name, no plate, no driverSessionId. That absent session id is what tells a
 * manual row from a driver's own. `opening` is what the office did know at
 * save time — the driver, plate, dock and status typed into the dispatch tab's
 * gate control while the row was still pending — so a truck waved in with its
 * paperwork in hand reaches the crew already named rather than as an anonymous
 * row somebody has to go back and fill in.
 *
 * Two writes, not the lambda's one transaction — AppSync has no transaction to
 * offer. The row goes in first and the shipment lock second, so a lost race
 * (a driver who checked in during the save) leaves a spare row we can retire
 * rather than a shipment pointing at a row that was never created.
 *
 * @param shipment the shipment as just saved
 * @param outbound direction, which decides every field name that differs
 * @param opening optional {status, driverName, truckPlate, dockAssignment} the
 *   office staged on the dispatch tab for the row it was checking in. A
 *   terminal status is not one a row can open at — the manager ended a visit
 *   that never started, so none is minted.
 * @returns the created (or adopted) row, or null when this shipment does not
 *   want one
 */
export const createManualGateCheckIn = async (shipment, outbound, opening) => {
  if (!shipment?.id || !shipment.reloadYardId) return null;
  const arrivedAt = arrivalStampOf(shipment, outbound);
  // Arrival stamp, placement and the shipment's own lock — the three tests the
  // dispatch tab reads through the same predicate, so the check-in it offers
  // and the row this mints can never disagree. The lock among them is only as
  // fresh as this save's data; the query below is what actually settles it, and
  // a driver checking in mid-save is exactly the case worth one extra read.
  if (!wantsManualGateCheckIn(shipment, outbound)) return null;
  // A row opens the way a kiosk row would (driverApi applies the same rule):
  // waiting on a yard with stops to be called through, or one that never
  // saved a list; CALLED on a yard that saved none — there is no line to
  // wait in.
  const openingStatus = opening?.status || sessionYardOpeningGateStatus();
  if (!GATE_ACTIVE_STATUSES.includes(openingStatus)) return null;

  const shipmentType = outbound ? "out" : "in";
  // A linked truck (LTL "ship with", outbound only) gets one row for every
  // load it carries. Its row may already sit under a sibling's key — the
  // driver searched that load — so every member's key is read.
  const linked = outbound && isLinked(shipment);
  const checkIns = await fetchShipmentCheckIns(
    shipmentType,
    shipment.id,
    linked ? shipment.shipWithIds : undefined,
  );
  const active = checkIns.find((c) => GATE_ACTIVE_STATUSES.includes(c.status));
  // An active row on a shipment holding no lock is this function's own orphan,
  // left by a claim that failed ambiguously. Adopt it — bailing instead would
  // make every retry return early while the driver API, which gates on the lock
  // alone, stays free to mint a second row for the same truck. A driver's own
  // row (driverSessionId) is never adopted: the lambda wrote it and its lock in
  // one transaction, so a missing lock there means the lock was released. A
  // group row is adopted for every load it names, so the members its failed
  // claim left free are claimed too.
  if (active) {
    if (active.driverSessionId) return null;
    const members = linked ? groupCheckInShipmentIds(active) : [shipment.id];
    return claimGroupLocks(
      shipment,
      active,
      outbound,
      members.length ? members : [shipment.id],
    );
  }

  // This visit already went through the gate. Ending a row leaves precisely
  // the shape read above as a truck wanting one — an arrival, no placement, no
  // lock — so without this test any later save of the shipment mints a fresh
  // ARRIVED for a visit the yard finished, and a status set from the Gate
  // Queue page is never seen by the shipment form at all.
  //
  // DEPARTED is final on its own: a worked truck does not come back through
  // the gate on the same shipment, and reading it that way keeps a manager
  // correcting the arrival time afterwards from re-queueing the truck.
  // CANCELLED is final only for the arrival it recorded —
  // rollbackCancelledArrival clears that stamp with it, and a manager who
  // re-enters an arrival afterwards is starting a visit that does want a row.
  // arrivedAt is that arrival on both sides: driverApi stamps one value into
  // the row and the shipment, and so does the create below. The cost of the
  // pairing is a cancelled visit re-entered at the identical time getting no
  // row; the alternative is the resurrection this test exists to stop.
  const endedVisit = checkIns.find(
    (c) =>
      c.status === GateCheckInStatus.DEPARTED ||
      (c.status === GateCheckInStatus.CANCELLED &&
        c.arrivedAt === arrivedAt),
  );
  if (endedVisit) return null;

  // A linked truck is checked in whole or not at all (AD-4): every sibling has
  // to be waiting for it. Read now, after the history says a row is wanted,
  // so a singleton pays for no extra read.
  //
  // Known gap, left open on purpose for now: the member list is fixed here,
  // and claimGroupLocks conditions each claim on the lock alone, not on the
  // membership it was read with. A link that commits between this read and
  // the first claim adds a load the row's outboundIds never name — it rides
  // the truck unlocked, stays PENDING when the rest advance, and cannot be
  // unlinked while the row is active. The kiosk cannot check it in twice (its
  // transact needs every member's lock free). The kiosk closes the same window
  // by conditioning each member write on shipWithGroupId/shipWithIds as read;
  // the fix here would be to re-read the saved load's shipWithIds after the
  // claims and, when it changed, release the locks and retire the row.
  const memberIds = linked ? await eligibleGroupMembers(shipment) : null;
  if (linked && !memberIds) return null;

  const created = await API.graphql(
    graphqlOperation(createGateCheckInMini, {
      input: withoutBlanks({
        reloadYardId: shipment.reloadYardId,
        groups: shipment.groups || null, // yard-group auth, as driverApi copies it
        shipmentKey: `${shipmentType}:${shipment.id}`,
        inboundId: outbound ? null : shipment.id,
        outboundId: outbound ? shipment.id : null,
        // Every load this row controls (LTL "ship with"); the saved load stays
        // the searched one above. Left off a single-load row, as the kiosk
        // leaves it.
        outboundIds: linked ? shipment.shipWithIds : null,
        carrierId: shipment.carrierId || null,
        carrierName: shipment.carrierName || null,
        timeslotId: shipment.timeslotId || null,
        // Half of the gate queue order; the manager Gate Queue lists check-in
        // rows and cannot re-read the shipment for it.
        scheduledDateTime: shipment.scheduledDateTime || null,
        // On a driver's row this is the text they typed to find the shipment.
        // Nobody typed anything here, so it carries what the office matched the
        // truck by instead — the field is required, and a blank one would say
        // less than the reference already printed on the paperwork.
        referenceEntered: String(
          shipment.poNumber ||
            shipment.pickupNumber ||
            shipment.railCarNumber ||
            "",
        ).slice(0, 64),
        // Trucks carry their trailer number in railCarNumber, the same field
        // the crew Gate Queue prints as "Trl #".
        trailerNumber: shipment.railCarNumber || null,
        // Whoever the office wrote down, if anyone: blanks are dropped rather
        // than stored, so an untouched field leaves the row as bare as before.
        driverName: opening?.driverName || null,
        truckPlate: opening?.truckPlate || null,
        dockAssignment: opening?.dockAssignment || null,
        status: openingStatus,
        // A row that opens CALLED was called when it was written — a yard
        // with no stops has no line to be called out of, and saveGateCheckIn
        // stamps calledAt on the ARRIVED -> CALLED move only, which such a
        // row never makes. Dated from the arrival for the same reason the
        // arrival itself is: the office is recording a truck already here,
        // not calling one now. driverApi stamps its kiosk rows the same way.
        calledAt: openingStatus === GateCheckInStatus.CALLED ? arrivedAt : null,
        // The arrival stamp, not now: arrivedAt is half of the queue order,
        // and dating a truck that arrived two hours ago as this minute would
        // send it to the back of a line it has been waiting in.
        arrivedAt,
        updatedBy: window.appUser?.email || null,
      }),
    }),
  );
  const checkIn = created?.data?.createGateCheckIn;
  if (!checkIn) return null;

  return claimGroupLocks(shipment, checkIn, outbound, memberIds || [shipment.id]);
};

/** The manager cleared the shipment's arrival stamp on the edit form, rolling
 * it back past ARRIVED — the check-in row passed in is now a phantom truck in
 * the crew Gate Queue and on the driver's phone. Cancel it. The shipment row
 * itself (status, arrival stamp, gateCheckInId) was written by the form save
 * that already committed, so only the check-in row is touched here — the
 * mirror image of rollbackCancelledArrival, which starts from the check-in
 * side. Conditioned on the status the row was read at; if the gate crew
 * advanced it meanwhile the row is re-read once and, when still active,
 * cancelled as it now stands — a rollback's cancel is not status-dependent.
 * Returns the row that was cancelled, or null when the crew already ended it.
 * Throws when the cancel cannot be confirmed, so the caller can surface the
 * phantom instead of leaving it on the Gate Queue unannounced. */
export const cancelGateCheckIn = async (checkIn, isRetry = false) => {
  try {
    await API.graphql(
      graphqlOperation(updateGateCheckInMini, {
        input: {
          id: checkIn.id,
          status: GateCheckInStatus.CANCELLED,
          // status heads the gateCheckInByYardByStatus composite sort key —
          // the update resolver requires every key field when one changes.
          arrivedAt: checkIn.arrivedAt,
          completedAt: new Date().toISOString(),
          updatedBy: window.appUser?.email || null,
        },
        // Write only against the status we read, so a crew edit landing
        // between the read and this write is never blindly overwritten.
        condition: { status: { eq: checkIn.status } },
      }),
    );
    return checkIn;
  } catch (err) {
    if (!isConditionalCheckFailed(err) || isRetry) throw err;
  }
  // Lost the race once: the crew moved the row. Ended by them — nothing left
  // to cancel; still active — cancel it at its current status. A linked
  // truck's row is read under every load it names, so it is found again
  // whichever of them this row was keyed on.
  const fresh = await fetchActiveGateCheckIn(
    checkIn.inboundId ? "in" : "out",
    checkIn.inboundId || checkIn.outboundId,
    checkIn.outboundIds?.length > 1 ? checkIn.outboundIds : undefined,
  );
  if (!fresh) return null;
  return cancelGateCheckIn(fresh, true);
};

/** Read-only half of persisting a status/dock edit the dispatch tab staged in
 * the form (gateCheckInEdit): find the row the edit targets and raise any
 * conflict BEFORE the shipment save commits, so a stale edit aborts the whole
 * save instead of surprising the manager after it. `checkInId` pins the row
 * (a new driver's check-in must not receive an edit meant for the old one)
 * and `baseStatus` is the status the manager was looking at when they staged
 * it. Returns the row to commit against, or null when the edit carries
 * nothing to persist. Throws GateCheckInConflictError when the row is gone,
 * was replaced, or moved past the staged base — here rather than at commit
 * time, so an edit the gate will refuse takes the shipment save down with it
 * instead of failing after it has committed.
 *
 * Queue order is NOT tested here. It is a refusal the supervisor has to be
 * told about, and this runs inside the form's transform, where a throw only
 * rejects react-final-form's submit promise — nothing catches that, so the
 * refusal reached the manager as an unhandled error instead of a warning. The
 * order is enforced where it can be reported: stagedGateEditRefusal before the
 * submit starts, and saveGateCheckIn at the write itself, whose refusal the
 * post-save gate step turns into a warning.
 *
 * `groupIds` is the shipment's `shipWithIds` when it is a linked load, so the
 * truck's row is found under whichever member the driver searched. */
export const resolveStagedGateEdit = async (
  shipmentType,
  shipmentId,
  gateEdit,
  groupIds,
) => {
  if (
    !gateEdit ||
    (!gateEdit.status &&
      gateEdit.dockAssignment === undefined &&
      gateEdit.driverName === undefined &&
      gateEdit.truckPlate === undefined)
  )
    return null;
  const activeCheckIn = await fetchActiveGateCheckIn(
    shipmentType,
    shipmentId,
    groupIds,
  );
  if (
    !activeCheckIn ||
    (gateEdit.checkInId && activeCheckIn.id !== gateEdit.checkInId) ||
    (gateEdit.baseStatus && activeCheckIn.status !== gateEdit.baseStatus)
  )
    throw new GateCheckInConflictError();
  return activeCheckIn;
};

/**
 * Why the gate edit staged in a shipment form cannot be saved — asked before
 * the form submits, and answered with a message to show rather than an error
 * to throw.
 *
 * Two refusals reach it. The queue order, which needs the queue re-read: the
 * status select on the dispatch tabs greys out the statuses the order forbids
 * and the Time Placed pickers refuse to stamp for the same reason, but both
 * read the queue once when the row is drawn. And the completion rule, which
 * needs nothing read at all — a visit only ends as DEPARTED from the dock,
 * and the status the manager was looking at is on the edit itself.
 *
 * Either way this is the last point a refusal can still stop the save
 * cleanly: past here the shipment write is in flight and the only honest
 * message left is "saved, but the gate status did not follow".
 *
 * Nothing about the staged edit is changed. The truck's turn comes, and the
 * same save then goes through — dropping the edit would leave the form holding
 * a placed time with no gate row to match it. A completion refusal does not
 * clear on its own, but the manager fixes it in the same select they staged it
 * in, and a dropped edit would take their dock and driver entries with it.
 *
 * A queue that cannot be read is logged and waved through, unlike
 * assertGateQueueTurn: this is the courtesy check, and the write behind it
 * still refuses an unproven turn.
 *
 * @returns {Promise<string|null>} the refusal to show the user, or null
 */
export const stagedGateEditRefusal = async (gateEdit, shipment = null) => {
  // No row pinned means the save is minting one (a truck the office waved in
  // at the gate); there is no queue place to jump, and createManualGateCheckIn
  // mints no row at a terminal status in the first place.
  if (!gateEdit?.status || gateEdit.manual || !gateEdit.checkInId) return null;
  // Judged against the status the edit was staged against; unpinned, there is
  // nothing to judge it from and the write guard settles it. `shipment` is the
  // form's own view of the load (record under form values), so a departure
  // staged for a load still being worked is refused before the save starts.
  if (gateEdit.baseStatus) {
    const departure = gateDepartureRefusal({
      status: gateEdit.status,
      currentStatus: gateEdit.baseStatus,
      shipment,
    });
    if (departure) return departure;
  }
  try {
    await assertGateQueueTurn(
      { id: gateEdit.checkInId, status: gateEdit.baseStatus },
      gateEdit.status,
    );
    return null;
  } catch (err) {
    if (err instanceof GateQueueOrderError) return err.message;
    logError("gateCheckInUtils:stagedGateEditRefusal", err, false);
    return null;
  }
};

/** Write half, run only AFTER the shipment save commits: a save that fails
 * midway (file upload, lambda write) must never leave the gate row advanced
 * ahead of a shipment that was not saved. Still conditioned on the staged
 * base status (`baseStatus` when pinned, else the status resolve read), so a
 * crew edit landing between resolve and commit raises the conflict instead
 * of being regressed — the caller warns, since the shipment is already in.
 * Returns the saved row. */
export const commitStagedGateEdit = async (checkIn, gateEdit) =>
  saveGateCheckIn(
    { ...checkIn, status: gateEdit.baseStatus ?? checkIn.status },
    {
      status: gateEdit.status || undefined,
      dockAssignment: gateEdit.dockAssignment,
      driverName: gateEdit.driverName,
      truckPlate: gateEdit.truckPlate,
    },
  );
